import React, { useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery } from '@apollo/client';
import { filterQuery, listQuery } from './queries';
import { dataToLovs, mapToListingData } from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IAbstractRecord } from '@models';
import _ from 'lodash';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useAppSelector } from '@redux/hooks';
import PersonDrawer from './person-drawer/PersonDrawer';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';
import DateService from '@services/DateService';
import { IEnhancedMenuItem } from '@components/table/types';
import { IFilterModel } from '@components/widgets/listing-widget';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';

const PAGE_CONTEXT = 'Persons';
const FILTER_SESSION_KEY = 'personsFilter';

const PersonsPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const user = useAppSelector((state) => state.user);
  const [personsDrawerOpen, setPersonsDrawerOpen] = useState<boolean>(false);
  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY).namedFilters
      : {
          type: [],
          status: [],
          createdOn: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: true,
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [loadPersonsLazy] = useLazyQuery(listQuery());
  const [filterQueryLazy] = useLazyQuery(filterQuery());

  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const actions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: '+ New',
          onClick: () => {
            setPersonsDrawerOpen(true);
          },
        },
      ]
    : [];

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      setLoading(false);
      return {};
    }

    setLoading(true);
    setFilter(filter, FILTER_SESSION_KEY);
    setFilterValues(filter);

    const { namedFilters, pagination } = filter;

    const formattedDateRange = DateService.formatDateRangeBackend(
      namedFilters.createdOn
    );
    return loadPersonsLazy({
      variables: {
        keywordSearch: filter.searchKey,
        currentPage: pagination.pageNumber || 1,
        currentPageSize: pagination.pageSize || 10,
        Attribute: capitalizeFirstCharacter(filter.orderBy || ''),
        Descending: filter.descending,
        selectedTypes: namedFilters.type || [],
        personStatus: namedFilters.status || null,
        createdDateFrom: formattedDateRange?.[0],
        createdDateTo: formattedDateRange?.[1],
      },
    })
      .then(({ data }) => {
        if (data) {
          setTableData(mapToListingData(data));
          filterQueryLazy().then(({ data: filterData }) => {
            setFilterSections(
              filterSectionsContent(dataToLovs(filterData), namedFilters)
            );
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = (
    page: number,
    filterModel = filterValues
  ): Promise<void> => {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  };

  const onFilterUpdate = (newFilters: IAbstractRecord) => {
    const updatedFilters = {
      ...filterValues,
      namedFilters: newFilters,
    };
    if (!_.isEqual(updatedFilters, filterValues)) {
      getListingData(updatedFilters);
    }
  };

  const renderMainChildren = () => (
    <>
      <ListingWidget
        title="Persons"
        name="persons"
        data={tableData}
        tableSettings={{ headers }}
        loading={loading}
        pageContext={PAGE_CONTEXT}
        onPageChange={(filterModel) => {
          const newFilterModel = {
            ...filterValues,
            ...filterModel,
          };
          const page = filterModel.pagination.pageNumber;
          return handlePageChange(page, newFilterModel);
        }}
        actions={actions}
        usePagination
        disableSelection
      />
      {personsDrawerOpen && (
        <PersonDrawer
          open={personsDrawerOpen}
          onClose={() => setPersonsDrawerOpen(false)}
        />
      )}
    </>
  );

  const renderFilter = () =>
    filterSections && (
      <ListingFilterWidget
        name=""
        filters={filterSections}
        onApplyFilter={onFilterUpdate}
        disabled={loading}
      />
    );

  return (
    <StaticLayout
      name="Persons"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default PersonsPage;
